import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ServiceDurationSelect from "~/components/booking/flow/ServiceDurationSelect";
import { SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS } from "~/components/booking/flow/confirmButton";
import { Button } from "~/components/ui/Button";
import { servicesCoveredByAllVouchers } from "~/lib/voucher-combination";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

const ServiceDurationStep = ({
  disableConfirm = false,
  onConfirm,
  onChange,
  onCancel,
  selectedDate,
  services,
  selectedAvailability,
  selectedCmsService,
  isVoucherBooking = false,
  defaultSelectedService,
  totalGuests,
}: {
  disableConfirm?: boolean;
  onChange: () => void;
  onCancel: () => void;
  onConfirm: (selectedDuration: any) => void;
  selectedDate: Date;
  services: any[];
  selectedAvailability: any;
  selectedCmsService: any;
  isVoucherBooking?: boolean;
  defaultSelectedService?: any;
  totalGuests?: number;
}) => {
  const { t } = useTranslation();
  const { bookingCart } = useBookingCartStore();
  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<any>(undefined);
  const hasAutoSkippedRef = useRef(false);

  const vouchers = bookingCart?.vouchers || [];

  useEffect(() => {
    setSelectedDuration(undefined);
    hasAutoSkippedRef.current = false;
  }, [services]);

  const filteredServices = (() => {
    if (vouchers.length === 0 || !isVoucherBooking) return services;

    const nonUpsellingVouchers = vouchers.filter(
      (v) => v.voucherData?.OnlyIntegration !== true,
    );

    if (nonUpsellingVouchers.length === 0) {
      return services.filter((s) => s.Type !== "Package");
    }

    const allIncludedCodes = nonUpsellingVouchers.reduce((acc: string[], v) => {
      const codes = v.voucherData?.ServicesIncludedCodes || [];
      return [...acc, ...codes];
    }, []);
    const voucherServicesIncluded = Array.from(new Set(allIncludedCodes));

    return servicesCoveredByAllVouchers(
      services.filter(
        (s) => s.Type !== "Package" && voucherServicesIncluded.includes(s.Code),
      ),
      nonUpsellingVouchers.map((v) => v.voucherData),
    );
  })();

  useEffect(() => {
    if (
      selectedCmsService &&
      filteredServices.length === 1 &&
      !hasAutoSkippedRef.current
    ) {
      hasAutoSkippedRef.current = true;
      onConfirm(filteredServices[0]);
    }
  }, [selectedCmsService, filteredServices, onConfirm]);

  if (selectedCmsService && filteredServices.length === 1) {
    return null;
  }

  if (hasErrors) {
    return (
      <div className="w-full bg-red-400 text-white p-4">
        <span className="text-white text-center block">
          {t("Qualcosa è andato storto! Ricarica la pagina e riprova")}
        </span>
      </div>
    );
  }

  return (
    <div
      id="step-service-duration"
      className="w-full max-w-3xl mx-auto flex flex-col px-8 py-8"
    >
      <h3 className="font-bold text-xl text-center">
        {(() => {
          const hasPackage = services?.some((s: any) => s.Type === "Package");
          return hasPackage
            ? t("Seleziona il servizio")
            : t("Durata dell'ingresso");
        })()}
      </h3>
      <p className="text-center mt-2">
        {(() => {
          const hasPackage = services?.some((s: any) => s.Type === "Package");
          return hasPackage
            ? t("Seleziona il servizio che desideri prenotare")
            : t("Seleziona la durata del tuo ingresso");
        })()}
      </p>
      {selectedCmsService && (
        <ServiceDurationSelect
          services={filteredServices || []}
          selectedCmsService={selectedCmsService}
          selectedValue={selectedDuration}
          isVoucherBooking={isVoucherBooking}
          totalGuests={totalGuests}
          onSelect={(duration) => {
            onChange();
            setSelectedDuration(duration);
            // Single-choice step: picking a card moves straight to the next step.
            if (!disableConfirm) {
              onConfirm(duration);
            }
          }}
        />
      )}

      {isLoading && (
        <div className="w-full flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      )}

      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        <Button
          variant={"muted"}
          onClick={() => {
            onCancel();
          }}
        >
          <span>{t("Indietro")}</span>
        </Button>

        {SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS && (
          <Button
            disabled={!selectedDuration || disableConfirm}
            variant={"secondary"}
            className="self-start"
            onClick={() => {
              //MOCK
              // let tmpSelectedDuration = selectedDuration;
              // tmpSelectedDuration.Availability =
              //   tmpSelectedDuration.Availability.map((availability: any) => ({
              //     ...availability,
              //     Quantity: {
              //       Total: 0,
              //     },
              //   }));
              // onConfirm(tmpSelectedDuration);
              //MOCK

              onConfirm(selectedDuration);
            }}
          >
            <span>{t("Conferma")}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ServiceDurationStep;
